import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import ImpersonatorNotice from '../components/ImpersonatorNotice';
import {
  ICON_RAIL_WIDTH_PX,
  MAIN_CONTENT_MAX_WIDTH_PX,
  MAIN_SIDEBAR_WIDTH_PX,
  MOBILE_TOP_BAR_HEIGHT_PX,
} from '../components/navigation/dimensions';
import MainSidebar from '../components/navigation/MainSidebar';
import SyncingNotice from '../components/SyncingNotice';
import { useFinancialAccounts } from '../hooks/queries/useFinancialAccounts';
import { useOnboardingState } from '../hooks/queries/useOnboardingState';
import { useFeatureGates } from '../hooks/useFeatureGates';
import { PATHS } from '../paths';

const HomeScene = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Settings is a left-anchored form surface; the rest of the app centers its
  // content column. Drop the centering on the settings route so the form hugs
  // the left edge instead of floating in the middle on wide viewports.
  const anchorContentLeft = location.pathname.startsWith(PATHS.SETTINGS);
  const { flags, loading: flagsLoading } = useFeatureGates();
  const isOnboardingV2 = flags.has(FeatureGate.OnboardingV2);

  // Both hooks fire unconditionally — React forbids conditional hooks, and
  // this branch collapses to one once `feat-onboarding-v2` graduates to
  // ALL_USERS and the legacy path is removed.
  const { financialAccounts, loading: financialAccountsLoading } = useFinancialAccounts();
  const { needsOnboarding, loading: onboardingStateLoading } = useOnboardingState();

  const checking =
    (flagsLoading && flags.size === 0) || (isOnboardingV2 ? onboardingStateLoading : financialAccountsLoading);
  const shouldOnboard = isOnboardingV2 ? needsOnboarding : financialAccounts.length === 0;

  React.useEffect(() => {
    if (!checking && shouldOnboard) {
      void navigate(PATHS.ONBOARDING);
    }
  }, [checking, shouldOnboard, navigate]);

  // Hold the shell until the check resolves and the about-to-redirect window
  // closes — otherwise the main app flashes in before the navigate-effect
  // above lands an onboarding-needing viewer at `/onboarding`.
  if (checking || shouldOnboard) {
    return null;
  }

  return (
    <>
      <MainSidebar />
      {/*
       * Outer wrapper reserves space for the fixed `MainSidebar` via padding
       * (top bar height below `md`, icon rail at `md`, full sidebar at `lg+`).
       * `flex-1` covers the case where this scene mounts inside a flex-row
       * parent — notably the Storybook global decorator
       * (`app-bg flex h-full w-full`). Without it the wrapper would shrink to
       * its content's intrinsic width and leave a right-side gutter at wide
       * viewports. In production the scene mounts directly inside `#root`
       * (block context, no flex parent), where `flex-1` is a no-op and
       * `h-full` + block-level width handle layout — but keeping `flex-1`
       * makes the layout robust to either context. `min-w-0` lets the inner
       * content shrink below intrinsic min-width so panes can reflow at
       * narrower viewports without overflowing right.
       *
       * Inner wrapper caps the pane at `MAIN_CONTENT_MAX_WIDTH_PX` and centers
       * it with `mx-auto` once the available width exceeds that cap. Below the
       * cap, the pane fills the available width.
       *
       * All four magic numbers come from `navigation/dimensions.ts` — change a
       * value there and the sidebar components, top bar, and these offsets all
       * update together.
       */}
      <div
        style={
          {
            '--nav-top-bar-h': `${MOBILE_TOP_BAR_HEIGHT_PX}px`,
            '--nav-rail-w': `${ICON_RAIL_WIDTH_PX}px`,
            '--nav-full-w': `${MAIN_SIDEBAR_WIDTH_PX}px`,
            '--nav-content-max-w': `${MAIN_CONTENT_MAX_WIDTH_PX}px`,
          } as React.CSSProperties
        }
        className="pt-(--nav-top-bar-h) md:pl-(--nav-rail-w) lg:pl-(--nav-full-w) flex h-full min-w-0 flex-1 flex-col md:pt-0"
      >
        <div
          className={cx(
            'max-w-(--nav-content-max-w) flex h-full w-full min-w-0 flex-col',
            !anchorContentLeft && 'mx-auto'
          )}
        >
          <div className="flex flex-col">
            <ImpersonatorNotice />
            <SyncingNotice />
          </div>
          {/*
           * No `overflow-hidden` here — individual panes manage their own
           * vertical scroll (see `EntityPaneBody`'s `overflow-y-auto`) and
           * horizontal containment (`overflow-x-hidden`). New panes that do
           * not extend `EntityPane` need to add their own containment, since
           * this wrapper deliberately allows the `h-full` chain to reach
           * them unblocked.
           *
           * `min-h-0` is load-bearing: html/body are `overflow: hidden` (see
           * ORN-548), so this flex item must not expand past its parent's
           * main-axis size, otherwise the pane's `h-full` resolves to the
           * intrinsic content height and `EntityPaneBody`'s `overflow-y-auto`
           * is dead. Without `min-h-0` the flex item defaults to
           * `min-height: auto` (= min-content) and grows past the viewport.
           */}
          <div className="flex min-h-0 min-w-0 flex-1">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomeScene;
